/**
 * Build the in-app destination URL for a notification based on its event type
 * and payload. Returns a string path, or null when there isn't enough context
 * to navigate.
 *
 * Shared by the notification dropdown (NotificationPanel) and the full
 * notifications page (NotificationsPage) so link behavior stays consistent.
 */
export function buildNotificationUrl(notification) {
  if (!notification) return null;

  const eventType = notification.event_type_key || '';
  const payload = notification.payload || {};
  // Study/site URLs use the public external UUID. Fall back to the legacy
  // integer ids for notifications emitted before the UUID cutover.
  const studyUrlId = payload.study_external_id || payload.study_id;
  const siteUrlId = payload.site_external_id || payload.site_id;
  const hasScope = studyUrlId && siteUrlId && payload.participant_id;

  if (eventType.startsWith('survey.') || eventType.startsWith('assessment.')) {
    // Prefer the fully-scoped completed-survey URL so the page resolves the
    // participant/study/site context (and its back button works).
    if (hasScope && payload.completed_survey_id) {
      return `/studies/${studyUrlId}/sites/${siteUrlId}/participant/${payload.participant_id}/completed-surveys/${payload.completed_survey_id}`;
    }
    if (hasScope) {
      return `/studies/${studyUrlId}/sites/${siteUrlId}/participant/${payload.participant_id}`;
    }
    if (payload.completed_survey_id) {
      return `/completed-surveys/${payload.completed_survey_id}`;
    }
    return null;
  }

  if (eventType.startsWith('participant.')) {
    if (hasScope) {
      return `/studies/${studyUrlId}/sites/${siteUrlId}/participant/${payload.participant_id}`;
    }
    return null;
  }

  if (eventType.startsWith('dcq.')) {
    if (studyUrlId && payload.dcq_id) {
      return `/studies/${studyUrlId}/dcq/${payload.dcq_id}`;
    }
    return null;
  }

  if (eventType.startsWith('study.')) {
    if (studyUrlId) {
      return `/studies/${studyUrlId}`;
    }
    return null;
  }

  if (eventType.startsWith('site.')) {
    if (studyUrlId && siteUrlId) {
      return `/studies/${studyUrlId}/sites/${siteUrlId}`;
    }
    return null;
  }

  if (eventType.startsWith('review.')) {
    // Study drafts open in the study design review page, resolved by draft name.
    if (payload.entity_type === 'study') {
      if (payload.entity_name) {
        return `/studybuilder/review/${encodeURIComponent(payload.entity_name)}`;
      }
      return '/studybuilder';
    }
    // Assessments: approver -> queue; submitter -> back to the item they own.
    if (eventType === 'review.submitted') {
      return '/assessment-builder/review-queue';
    }
    if (payload.entity_uuid) {
      return `/assessment-builder/${payload.entity_uuid}`;
    }
    return '/assessment-builder/review-queue';
  }

  return null;
}

/**
 * Human-friendly label for a notification event type key.
 * e.g. "survey.missed" -> "Survey · Missed"
 */
export function formatEventType(eventTypeKey) {
  if (!eventTypeKey) return 'Notification';
  return eventTypeKey
    .split('.')
    .map((part) => part.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' · ');
}

/**
 * Tailwind color classes (bg/text) for a notification domain, keyed off the
 * event type prefix. Used for badges/icons.
 */
export function eventTypeColor(eventTypeKey = '') {
  if (eventTypeKey.startsWith('survey.') || eventTypeKey.startsWith('assessment.')) {
    return { badge: 'bg-blue-100 text-blue-700', dot: 'text-blue-500' };
  }
  if (eventTypeKey.startsWith('participant.')) {
    return { badge: 'bg-green-100 text-green-700', dot: 'text-green-500' };
  }
  if (eventTypeKey.startsWith('dcq.')) {
    return { badge: 'bg-purple-100 text-purple-700', dot: 'text-purple-500' };
  }
  if (eventTypeKey.startsWith('study.')) {
    return { badge: 'bg-amber-100 text-amber-700', dot: 'text-amber-500' };
  }
  if (eventTypeKey.startsWith('site.')) {
    return { badge: 'bg-teal-100 text-teal-700', dot: 'text-teal-500' };
  }
  if (eventTypeKey.startsWith('review.')) {
    return { badge: 'bg-indigo-100 text-indigo-700', dot: 'text-indigo-500' };
  }
  return { badge: 'bg-gray-100 text-gray-700', dot: 'text-gray-500' };
}
